import { useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';
import type {
  FormikErrors,
  FormikHelpers,
  FormikState,
  FormikTouched,
} from 'formik';

// MHO : I created this hook as a workaround for following issue :
// When you aren't in strict mode (ie production), Formik setFieldValue used in useEffect
// doesn't work in ResolvableServiceInfoFormStep : TEMPORARY_STRING is not replaced by EMPTY_STRING
// on first render and is still visible to user.
// I suspect this issue to be encoutered in several other places in the app.
// So I generalized the workaround in this hook.

/**
 * Execute Formik operations on the next tick using setTimeout.
 * Useful when you need to ensure state updates have been applied before running Formik operations.
 */
export const executeFormikOnNextTick = <V,>(
  formikHelpers: FormikHelpers<V>,
  operation: (helpers: FormikHelpers<V>) => void,
) => {
  setTimeout(() => {
    operation(formikHelpers);
  }, 0);
};

/**
 * Hook that wraps all Formik helpers to execute on next tick.
 * Useful in components where multiple Formik operations need to be deferred.
 *
 * @example
 * const formik = useFormikOnNextTick<MyFormValues>();
 * // All methods will execute on next tick
 * formik.setFieldValue('name', 'John');
 * formik.setErrors({});
 * formik.validateForm();
 */
export const useFormikOnNextTick = <V,>() => {
  const formikContext = useFormikContext<V>();

  // Wrap individual methods with useCallback to keep them stable
  const setFieldValue = useCallback(
    (field: string, value: unknown) => {
      setTimeout(() => {
        void formikContext.setFieldValue(field, value);
      }, 0);
    },
    // setFieldValue from Formik is stable, but eslint doesn't know that
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setFieldTouched = useCallback(
    (field: string, touched?: boolean, shouldValidate?: boolean) => {
      setTimeout(() => {
        void formikContext.setFieldTouched(field, touched, shouldValidate);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setFieldError = useCallback(
    (field: string, message: string | undefined) => {
      setTimeout(() => {
        formikContext.setFieldError(field, message);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setErrors = useCallback(
    (errors: FormikErrors<V>) => {
      setTimeout(() => {
        formikContext.setErrors(errors);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setTouched = useCallback(
    (touched: FormikTouched<V>, shouldValidate?: boolean) => {
      setTimeout(() => {
        void formikContext.setTouched(touched, shouldValidate);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setValues = useCallback(
    (values: V | ((prevValues: V) => V), shouldValidate?: boolean) => {
      setTimeout(() => {
        void formikContext.setValues(values, shouldValidate);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const validateForm = useCallback(
    (values?: V) => {
      setTimeout(() => {
        void formikContext.validateForm(values);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const validateField = useCallback(
    (field: string) => {
      setTimeout(() => {
        void formikContext.validateField(field);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const resetForm = useCallback(
    (nextState?: Readonly<Partial<FormikState<V>>>) => {
      setTimeout(() => {
        formikContext.resetForm(nextState);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const submitForm = useCallback(
    () => {
      setTimeout(() => {
        void formikContext.submitForm();
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setSubmitting = useCallback(
    (isSubmitting: boolean) => {
      setTimeout(() => {
        formikContext.setSubmitting(isSubmitting);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setStatus = useCallback(
    (status: unknown) => {
      setTimeout(() => {
        formikContext.setStatus(status);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Return only the wrapped methods to keep the object stable
  // Don't spread formikContext to avoid recreating on every render
  return useMemo(
    () => ({
      setFieldValue,
      setFieldTouched,
      setFieldError,
      setErrors,
      setTouched,
      setValues,
      validateForm,
      validateField,
      resetForm,
      submitForm,
      setSubmitting,
      setStatus,
    }),
    [
      setFieldValue,
      setFieldTouched,
      setFieldError,
      setErrors,
      setTouched,
      setValues,
      validateForm,
      validateField,
      resetForm,
      submitForm,
      setSubmitting,
      setStatus,
    ],
  );
};
